// Angular imports
import { Injectable, inject } from '@angular/core';

// Project libraries
import { GqlService } from '@tgx/shared/data-access/graphql';
import { BillingAccountContactCreateInput } from '@tgx/shared/interfaces';

// Local imports
import { BillingContractCreate, CreateContractOutput } from '../interface/billing-contract';
import {
  BillingAccount,
  BillingAccountContact,
  BillingAccountCreate,
  BillingAccountUpdate,
  BillingSubTypes,
  CreateBillingAccountOutput,
  Partner,
  UpdateBillingAccountOutput,
} from '../interface/billing-account';
import { createBillingAccount } from '../operations/mutations/create-billing-account';
import { createBillingContact } from '../operations/mutations/create-billing-contact';
import { CreateBillingContract } from '../operations/mutations/create-billing-contract';
import { CreatePartner } from '../operations/mutations/create-billing-partner';
import { updateBillingAccount } from '../operations/mutations/update-billing-account';
import { getBillingAccounts } from '../operations/queries/get-billing-accounts';
import { getBillingSubTypes } from '../operations/queries/get-billing-sub-types';
import { RetrieveBillingAccountContacts } from '../operations/queries/retrieve-billing-account-contacts';
import { RetrievePartner } from '../operations/queries/retrieve-billing-partner';

@Injectable({ providedIn: 'root' })
export class OnboardingBillingService {
  private gqlService = inject(GqlService);

  retrievePartner(orgCode: string): Promise<Partner> {
    const body = { data: { orgCode: orgCode.toUpperCase() } };

    return new Promise((resolve, reject) => {
      this.gqlService
        .queryBilling<{ partner: Partner }>(RetrievePartner, body)
        .then((response) => {
          resolve(response.partner);
        })
        .catch((err) => reject(err));
    });
  }

  createPartner(orgCode: string, orgLabel: string): Promise<Partner> {
    const body = { data: { name: orgLabel, orgCode: orgCode.toUpperCase() } };

    return this.gqlService
      .mutationBilling<{ createPartner: Partner }>(CreatePartner, body)
      .then((response) => response.createPartner)
      .catch((err) => Promise.reject(err));
  }

  createBillingAccount(data: BillingAccountCreate): Promise<BillingAccount> {
    const body = { data: data };
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationBilling<CreateBillingAccountOutput>(createBillingAccount, body)
        .then((res) => {
          const errMsg = 'There is a problem while create billing';

          if (res && res?.createBillingAccount) {
            resolve(res.createBillingAccount);
          } else if (res?.errors?.length > 0) {
            reject(res.errors[0]);
          } else {
            reject(errMsg);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  updateBillingAccount(where, data: BillingAccountUpdate): Promise<BillingAccount> {
    const body = { data: data, where };
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationBilling<UpdateBillingAccountOutput>(updateBillingAccount, body)
        .then((res) => {
          const errMsg = 'There is a problem while updating billing';

          if (res && res?.updateBillingAccount) {
            resolve(res.updateBillingAccount);
          } else if (res?.errors?.length > 0) {
            reject(res.errors[0]);
          } else {
            reject(errMsg);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  retrieveBillingAccountContacts(orgCode: string): Promise<BillingAccountContact[]> {
    const data = { BillingAccount: { is: { Partner: { is: { orgCode: { equals: orgCode } } } } } };
    return new Promise((resolve, reject) => {
      this.gqlService
        .queryBilling<{ billingAccountContacts: BillingAccountContact[] }>(RetrieveBillingAccountContacts, { data })
        .then((response) => {
          if (response && response?.billingAccountContacts) {
            resolve(response.billingAccountContacts);
          } else {
            reject(new Error('Billing contacts not found'));
          }
        })
        .catch((error) => reject(error));
    });
  }

  createBillingContact(data: BillingAccountContactCreateInput): Promise<BillingAccountContact> {
    const body = { data: data };
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationBilling<{ createBillingAccountContact: BillingAccountContact }>(createBillingContact, body)
        .then((res) => {
          if (res && res?.createBillingAccountContact) {
            resolve(res.createBillingAccountContact);
          } else {
            reject(res);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  retrieveBillingSubTypes(): Promise<BillingSubTypes[]> {
    return new Promise((resolve, reject) => {
      this.gqlService
        .queryBilling<{ contractTypes: BillingSubTypes[] }>(getBillingSubTypes)
        .then((resp) => {
          if (resp && resp?.contractTypes) {
            resolve(resp.contractTypes);
          } else {
            reject(resp);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  createContract(createData: BillingContractCreate): Promise<number> {
    return new Promise((resolve, reject) => {
      const body = { data: createData };
      this.gqlService
        .mutationBilling<{ createContract: CreateContractOutput }>(CreateBillingContract, body)
        .then((response) => {
          resolve(response.createContract?.id);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getBillingAccounts(orgCode: string): Promise<BillingAccount> {
    const where = { Partner: { is: { orgCode: { equals: orgCode.toUpperCase() } } } };
    return new Promise((resolve, reject) => {
      this.gqlService
        .queryBilling<{ billingAccounts: BillingAccount }>(getBillingAccounts, { where: where })
        .then((res) => {
          if (res && res?.billingAccounts) {
            resolve(res.billingAccounts);
          } else {
            reject(res);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
